import { useMutation, useQueryClient } from "@tanstack/react-query";
import { sendChatMessage } from "../services/discuss.service";
import { ChatMessage } from "../types/discuss.types";

/**
 * Hook to send a chat message and get AI response
 */
export const useSendMessage = (documentId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: sendChatMessage,
    mutationKey: ["sendChatMessage", documentId],
    onMutate: async (newChatMessage) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: ["chatHistory", documentId] });

      // Snapshot the previous value
      const previousChatHistory = queryClient.getQueryData(["chatHistory", documentId]);

      // Optimistically update to the new value
      queryClient.setQueryData(["chatHistory", documentId], (oldData: any) => {
        const newUserMessage: ChatMessage = {
          question: newChatMessage.data,
          response: "",
          creation_date: new Date().toISOString(),
          related_questions: [],
        };
        const newPrevChat = oldData?.prev_chat
          ? [...oldData.prev_chat, newUserMessage]
          : [newUserMessage];
        return {
          ...oldData,
          prev_chat: newPrevChat,
        };
      });

      return { previousChatHistory };
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(["chatHistory", documentId], (oldData: any) => {
        const updatedMessages = [...(oldData?.prev_chat || [])];
        const lastMessage = updatedMessages[updatedMessages.length - 1];
        if (lastMessage && lastMessage.question === variables.data) {
          lastMessage.response = data.response;
          lastMessage.creation_date = data.creation_date;
          lastMessage.related_questions = data.related_questions;
        }
        return {
          ...oldData,
          prev_chat: updatedMessages,
        };
      });
    },
    onError: (error, newChatMessage, context) => {
      console.error("Failed to send message:", error);
      // If the mutation fails, use the context to roll back
      queryClient.setQueryData(["chatHistory", documentId], context?.previousChatHistory);
      // Optionally, update the last message with an error response
      queryClient.setQueryData(["chatHistory", documentId], (oldData: any) => {
        const updatedMessages = [...(oldData?.prev_chat || [])];
        const lastMessage = updatedMessages[updatedMessages.length - 1];
        if (lastMessage && lastMessage.question === newChatMessage.data) {
          lastMessage.response =
            "Sorry, I encountered an error while processing your message. Please try again.";
        }
        return {
          ...oldData,
          prev_chat: updatedMessages,
        };
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["chatHistory", documentId] });
    },
  });
};
